import { CdkTextareaAutosize } from '@angular/cdk/text-field'
import { CommonModule } from '@angular/common'
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  Injector,
  OnInit,
  Signal,
  computed,
  inject,
  input,
  runInInjectionContext,
  viewChild,
} from '@angular/core'
import { FormControl, ReactiveFormsModule, ValidationErrors } from '@angular/forms'

import { FlxStyleModule, FlxThemeModule } from '@flowx/angular-theme'
import { FlxIconModule, FlxTextareaModule } from '@flowx/angular-ui-toolkit'
import { LabelAccessibilityProps, TextareaTemplateConfig } from '@flowx/core-sdk'
import { ThemeComponent } from '@flowx/core-theme'

import { FlxLocalizePipe } from '../../pipes/flx-localize.pipe'
import { getDynamicPropSignal, getErrorMessages, getThemeIcon } from '../helpers'

const MIN_ALLOWED_ROWS = 2

@Component({
  selector: 'flx-textarea-wrapper',
  imports: [
    CommonModule,
    FlxTextareaModule,
    FlxLocalizePipe,
    ReactiveFormsModule,
    CdkTextareaAutosize,
    FlxThemeModule,
    FlxStyleModule,
    FlxIconModule,
  ],
  templateUrl: './textarea-wrapper.component.html',
  styleUrl: './textarea-wrapper.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FlxTextareaWrapperComponent implements OnInit, AfterViewInit {
  #injector = inject(Injector)
  templateConfig = input.required<TextareaTemplateConfig>()
  control = input.required<FormControl>()
  errors = input<ValidationErrors | null>(null)
  showErrors = input<boolean>(false)
  disabledState = input<boolean>(false)

  cdkTextareaAutosize = viewChild(CdkTextareaAutosize)

  displayOptions = computed(() => this.templateConfig().displayOptions)
  flowxProps = computed(() => this.displayOptions().flowxProps)
  minRows = computed(() => this.flowxProps().minRows || MIN_ALLOWED_ROWS)
  maxRows = computed(() => this.flowxProps().maxRows || 100)
  heightType = computed(() => this.displayOptions()?.style?.heightType || 'auto')
  accessibility = computed((): LabelAccessibilityProps | null => this.flowxProps()?.accessibility)

  icons = computed(() => ({
    clear: getThemeIcon(ThemeComponent.Textarea, 'clearIcon'),
    helper: getThemeIcon(ThemeComponent.HelperTooltip, 'helperIcon'),
  }))

  // dynamic props
  label: Signal<string>
  placeholder: Signal<string>
  helper: Signal<string>
  errorMessages: Record<string, Signal<string>>
  ariaLabel: Signal<string>

  readonly elementUniqueId = `${crypto.randomUUID()}`
  readonly itemId = `${this.elementUniqueId}-form-item`
  readonly helperId = `${this.elementUniqueId}-helper`
  readonly errorId = `${this.elementUniqueId}-error`

  readonly describedByIds = computed(() => {
    const ids: string[] = []
    const helper = this.helper()
    const showErrors = this.showErrors()
    const errors = this.errors()

    if (showErrors && errors) {
      Object.keys(errors).forEach((_, index) => {
        ids.push(`${this.errorId}${index}`)
      })
    } else if (helper) {
      ids.push(this.helperId)
    }
    return ids.join(' ')
  })

  readonly CLEAR_LABEL = '@@sys_clear'

  ngOnInit(): void {
    // we need to run this in the injection context here because templateConfig is not available in the constructor;
    // also, running in injectionContext will make sure that the observables are properly disposed when the component is destroyed
    runInInjectionContext(this.#injector, () => {
      const config = this.templateConfig()
      const { label, placeholder, helper } = this.flowxProps()
      this.label = getDynamicPropSignal(label, this.templateConfig())
      this.placeholder = getDynamicPropSignal(placeholder, this.templateConfig())
      this.helper = getDynamicPropSignal(helper, this.templateConfig())
      this.ariaLabel = getDynamicPropSignal(this.accessibility()?.label, config)

      this.errorMessages = getErrorMessages(config)
    })
  }

  ngAfterViewInit(): void {
    // wait for custom styles to compute and force resize
    setTimeout(() => this.cdkTextareaAutosize()?.resizeToFitContent(true))
  }
}
